import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clipboard, FileText, HardDrive, PlusCircle, Bell, ExternalLink, ChevronRight, UserCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { actasAPI, activosAPI, notificacionesAPI, ticketsAPI, usuariosAPI } from '../services/api';
import { LOGIN_CENTRAL_URL } from '../config';
import LogoApp from '../components/LogoApp';
import EstadoActaCell from '../components/EstadoActaCell';
import { haceCuanto } from '../utils/tiempo';

const ESTADOS_ABIERTOS = new Set(['pendiente', 'asignado', 'en proceso', 'esperando usuario', 'esperando aprobacion']);
const COLOR_ESTADO = {
  pendiente: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200',
  asignado: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
  'en proceso': 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  'esperando usuario': 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200',
  resuelto: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  cerrado: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
};

const lista = (r) => (r.status === 'fulfilled' ? (Array.isArray(r.value.data) ? r.value.data : r.value.data?.items || r.value.data?.notificaciones || []) : []);

function Kpi({ icono: Icono, etiqueta, valor, detalle, onClick, tono = 'azul' }) {
  const tonos = {
    azul: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600',
    ambar: 'bg-amber-50 dark:bg-amber-900/20 text-amber-600',
    verde: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600',
  };
  return (
    <button type="button" onClick={onClick} className="text-left bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 flex items-center gap-3 shadow-sm hover:shadow-md hover:border-blue-300 dark:hover:border-blue-700 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
      <span className={`p-2.5 rounded-lg shrink-0 ${tonos[tono]}`}><Icono className="w-5 h-5" /></span>
      <span className="min-w-0">
        <span className="block text-xs text-gray-500 dark:text-gray-400">{etiqueta}</span>
        <span className="block text-2xl font-bold text-gray-900 dark:text-white leading-tight">{valor}</span>
        {detalle && <span className="block text-[11px] text-gray-400 truncate">{detalle}</span>}
      </span>
    </button>
  );
}

function Panel({ titulo, accion, onAccion, children }) {
  return (
    <section className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-white">{titulo}</h2>
        {accion && <button type="button" onClick={onAccion} className="text-xs font-semibold text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center">{accion}<ChevronRight className="w-3.5 h-3.5" /></button>}
      </div>
      {children}
    </section>
  );
}

/**
 * Dashboard de las personas que no son admin ni staff: solo su información (sus tickets, equipos, actas,
 * avisos y aplicaciones). No consulta nada de gestión.
 */
export default function DashboardPersonal() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [cargando, setCargando] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [activos, setActivos] = useState([]);
  const [actas, setActas] = useState([]);
  const [avisos, setAvisos] = useState([]);
  const [apps, setApps] = useState([]);

  useEffect(() => {
    let cancelado = false;
    Promise.allSettled([
      ticketsAPI.getMisTickets(),
      activosAPI.getMisActivos(),
      actasAPI.getMisActas(),
      notificacionesAPI.getMisNotificaciones(),
      usuariosAPI.getMisApps(),
    ]).then(([t, a, ac, n, ap]) => {
      if (cancelado) return;
      setTickets(lista(t));
      setActivos(lista(a));
      setActas(lista(ac));
      setAvisos(lista(n));
      setApps(lista(ap).filter((x) => x.codigo !== 'helpdesk'));
      setCargando(false);
    });
    return () => { cancelado = true; };
  }, []);

  const abiertos = useMemo(() => tickets.filter((t) => ESTADOS_ABIERTOS.has(String(t.estado || '').toLowerCase())), [tickets]);
  const esperandoMe = useMemo(() => tickets.filter((t) => String(t.estado || '').toLowerCase() === 'esperando usuario'), [tickets]);
  const recientes = useMemo(() => [...tickets].sort((x, y) => new Date(y.fechaCreacion || 0) - new Date(x.fechaCreacion || 0)).slice(0, 5), [tickets]);
  const actasPendientes = useMemo(() => actas.filter((a) => a.estado === 'Pendiente'), [actas]);
  const sinLeer = useMemo(() => avisos.filter((n) => !n.isRead), [avisos]);

  const actaDe = (codigo) => actas.find((a) => a.asignacion?.activo?.codigo === codigo) || null;

  if (cargando) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" /></div>;
  }

  return (
    <div className="space-y-6 pb-8 max-w-7xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Hola, {user?.nombre || 'bienvenido'}</h1>
          <p className="text-gray-600 dark:text-gray-400">Este es el resumen de tus solicitudes, equipos y documentos.</p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/mis-tickets?action=new')}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-sm"
        >
          <PlusCircle className="w-5 h-5" />Crear ticket
        </button>
      </div>

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        <Kpi icono={Clipboard} etiqueta="Tickets abiertos" valor={abiertos.length} detalle={`${tickets.length} en total`} onClick={() => navigate('/mis-tickets')} />
        <Kpi icono={Clipboard} etiqueta="Esperan tu respuesta" valor={esperandoMe.length} tono={esperandoMe.length > 0 ? 'ambar' : 'azul'} detalle={esperandoMe.length > 0 ? 'Confírmalos o respóndelos' : 'Nada pendiente'} onClick={() => navigate('/mis-tickets')} />
        <Kpi icono={HardDrive} etiqueta="Mis equipos" valor={activos.length} detalle="Asignados a ti" tono="verde" onClick={() => navigate('/mis-activos')} />
        <Kpi icono={FileText} etiqueta="Actas por firmar" valor={actasPendientes.length} tono={actasPendientes.length > 0 ? 'ambar' : 'azul'} detalle={actasPendientes.length > 0 ? 'Revísalas y fírmalas' : 'Al día'} onClick={() => navigate('/actas')} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 items-start">
        <div className="xl:col-span-2 space-y-4">
          <Panel titulo="Mis tickets recientes" accion="Ver todos" onAccion={() => navigate('/mis-tickets')}>
            {recientes.length === 0 ? (
              <div className="text-center py-6">
                <p className="text-sm text-gray-500 dark:text-gray-400">Todavía no has creado tickets.</p>
                <button type="button" onClick={() => navigate('/mis-tickets?action=new')} className="mt-2 text-sm font-semibold text-blue-600 hover:underline">Crear tu primer ticket</button>
              </div>
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                {recientes.map((t) => (
                  <li key={t.id}>
                    <button type="button" onClick={() => navigate(`/tickets/${t.id}`)} className="w-full flex items-center justify-between gap-3 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700/40 rounded-lg px-2 -mx-2">
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-gray-900 dark:text-white truncate">#{t.id} · {t.descripcion || 'Sin descripción'}</span>
                        <span className="block text-xs text-gray-500">{haceCuanto(t.fechaCreacion)}{t.asignadoA ? ` · atiende ${t.asignadoA.nombre || ''}`.trimEnd() : ''}</span>
                      </span>
                      <span className={`shrink-0 px-2 py-0.5 rounded-full text-[11px] font-semibold ${COLOR_ESTADO[String(t.estado || '').toLowerCase()] || COLOR_ESTADO.cerrado}`}>{t.estado}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Panel>

          <Panel titulo="Mis equipos" accion="Ver detalle" onAccion={() => navigate('/mis-activos')}>
            {activos.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 py-2">No tienes equipos asignados.</p>
            ) : (
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {activos.slice(0, 6).map((a) => (
                  <li key={a.codigo} className="flex items-center justify-between gap-3 rounded-lg border border-gray-100 dark:border-gray-700 p-3">
                    <span className="min-w-0">
                      <span className="block text-sm font-bold text-gray-900 dark:text-white truncate">{a.codigo}</span>
                      <span className="block text-xs text-gray-500 truncate">{a.categoria}{a.marca || a.modelo ? ` · ${[a.marca, a.modelo].filter(Boolean).join(' ')}` : ''}</span>
                    </span>
                    <EstadoActaCell acta={actaDe(a.codigo)} />
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>

        <div className="space-y-4">
          <Panel titulo="Avisos" accion={sinLeer.length > 0 ? `${sinLeer.length} sin leer` : undefined}>
            {avisos.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 py-2 flex items-center gap-2"><Bell className="w-4 h-4" />No tienes avisos.</p>
            ) : (
              <ul className="space-y-3">
                {avisos.slice(0, 5).map((n) => (
                  <li key={n.id}>
                    <button type="button" onClick={() => n.ruta && navigate(n.ruta)} className="w-full text-left">
                      <span className={`block text-sm ${n.isRead ? 'text-gray-600 dark:text-gray-300' : 'font-semibold text-gray-900 dark:text-white'}`}>{n.titulo}</span>
                      {n.mensaje && <span className="block text-xs text-gray-500 line-clamp-2">{n.mensaje}</span>}
                      <span className="block text-[11px] text-gray-400 mt-0.5">{haceCuanto(n.createdAt)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Panel>

          {apps.length > 0 && (
            <Panel titulo="Mis aplicaciones">
              <ul className="grid grid-cols-2 gap-3">
                {apps.map((a) => (
                  <li key={a.codigo}>
                    <button
                      type="button"
                      onClick={() => window.open(`${LOGIN_CENTRAL_URL}/?app=${encodeURIComponent(a.codigo)}`, '_blank', 'noopener,noreferrer')}
                      className="w-full flex flex-col items-center gap-2 p-3 rounded-lg border border-gray-100 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-700 hover:bg-gray-50 dark:hover:bg-gray-700/40 text-center transition-colors"
                    >
                      <LogoApp codigo={a.codigo} tam="w-10 h-10" />
                      <span className="text-xs font-semibold text-gray-800 dark:text-gray-200 leading-tight">{a.nombre}<ExternalLink className="inline w-3 h-3 ml-1 text-gray-400" /></span>
                    </button>
                  </li>
                ))}
              </ul>
            </Panel>
          )}

          <Panel titulo="Mi cuenta">
            <button type="button" onClick={() => navigate('/perfil')} className="w-full flex items-center gap-3 text-left">
              <span className="p-2 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500"><UserCircle className="w-6 h-6" /></span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-gray-900 dark:text-white truncate">{[user?.nombre, user?.apellido].filter(Boolean).join(' ') || user?.username}</span>
                <span className="block text-xs text-gray-500 truncate">{user?.email}</span>
              </span>
            </button>
          </Panel>
        </div>
      </div>
    </div>
  );
}
